import { z } from 'zod';

/**
 * Аватар и верификация личности (§8-9).
 *
 * Создание цифровой копии реального человека требует consent/identity flow.
 * Приоритет — полностью внутренний сценарий; редирект к внешней проверке
 * допускается как фолбэк (§9, вопрос A5).
 */

/** Ровно восемь статусов §8. */
export const AvatarStatus = {
  NOT_STARTED: 'not_started',
  CAPTURED: 'captured',
  UPLOADING: 'uploading',
  CREATING: 'creating',
  CONSENT_REQUIRED: 'consent_required',
  VERIFYING: 'verifying',
  READY: 'ready',
  FAILED: 'failed',
} as const;
export type AvatarStatus = (typeof AvatarStatus)[keyof typeof AvatarStatus];

/** Тексты §52: ни кодов, ни имён сервисов. */
export const avatarStatusLabels: Record<AvatarStatus, string> = {
  not_started: 'Не создан',
  captured: 'Материал записан',
  uploading: 'Загружается',
  creating: 'Создаётся',
  consent_required: 'Нужно подтверждение',
  verifying: 'Проверяется',
  ready: 'Готов',
  failed: 'Не удалось создать',
};

/** Что пользователь может сделать прямо сейчас. */
export const avatarStatusHints: Record<AvatarStatus, string> = {
  not_started: 'Запишите видео — из него создадутся аватар и голос.',
  captured: 'Запись принята, скоро начнём.',
  uploading: 'Передаём материалы.',
  creating: 'Обычно это занимает от нескольких минут до пары часов.',
  consent_required: 'Подтвердите, что создаёте копию себя.',
  verifying: 'Проверяем подтверждение — обычно недолго.',
  ready: 'Аватар готов к съёмке.',
  failed: 'Попробуйте записать видео ещё раз.',
};

export interface AvatarProfileView {
  id: string;
  status: AvatarStatus;
  label: string;
  hint: string;
  errorCode: string | null;
  previewUrl: string | null;
  /** Нужна ли consent-запись прямо сейчас (§9). */
  consentRequired: boolean;
  /** Заполняется, только если white-label невозможен и нужен переход (§9, A5). */
  externalVerificationUrl: string | null;
  createdAt: string;
  readyAt: string | null;
}

/**
 * Текст согласия, который пользователь произносит на камеру (§9).
 * Версионируется вместе с документом согласия.
 */
export interface ConsentStatement {
  version: string;
  text: string;
  instructions: string[];
  minSec: number;
  maxSec: number;
}

export const consentStatement: ConsentStatement = {
  version: '1.0',
  text:
    'Я подтверждаю, что это я, и разрешаю создать цифровую копию моей внешности и голоса ' +
    'для использования в моих собственных видео.',
  instructions: [
    'Смотрите в камеру',
    'Произнесите фразу вслух и целиком',
    'Лицо должно быть полностью видно',
  ],
  minSec: 3,
  maxSec: 30,
};

export const submitConsentSchema = z
  .object({
    assetId: z.string().min(1),
    statementVersion: z.string().min(1).optional(),
  })
  .strict();

export const createAvatarSchema = z.object({}).strict();
